import {
  CheckOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  FileExclamationOutlined,
  ReloadOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { Spin, Tag, theme, Tooltip, Typography, Upload, Button } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { DataTable } from 'components/DataTable'
import { useStableUsRestrictedDataAccess } from 'enrollment-platform/rbac/useStableUsRestrictedDataAccess'
import useFeatureFlags from 'hooks/useFeatureFlags'
import type {
  AnalysisFileValidationIssuesDto,
  OepDocumentDocType,
  OepDocumentProcessingStatus,
} from 'modules/annual-benefit-planning/modules/approval/infra/api/config.api'
import { useGetPolicyQuery } from 'modules/annual-benefit-planning/modules/policy/application'
import { APPROVAL_QUERY_KEYS } from 'modules/annual-benefit-planning/modules/approval/infra/react-query/approval.query-keys'
import { OEPFormatters } from 'modules/annual-benefit-planning/modules/shared/presentation/utils/open-enrollment-formatter'
import { AnalysisFileDetailModal } from './AnalysisFileDetailModal'
import { AnalysisValidationIssuesModal } from './AnalysisValidationIssuesModal'
import validationIssuesStyles from './AnalysisValidationIssuesModal.module.scss'
import { DeleteConfirmationModal } from './DeleteConfirmationModal'
import { holdWindowScroll } from './holdWindowScroll'
import { useAbp5FileJobs } from '../hooks/useAbp5FileJobs'
import { useOepDocumentList } from '../hooks/useOepDocumentList'
import { useOepDocumentProcessingStatus } from '../hooks/useOepDocumentProcessingStatus'
import { useOepDocumentRowActions } from '../hooks/useOepDocumentRowActions'
import { useOepDocumentUpload } from '../hooks/useOepDocumentUpload'
import type { OepTargetFileJob, OepUploadState } from '../types'
import {
  ABP5_GENERATION_FAILED_MESSAGE,
  CONTRIBUTION_GENERATED_WITH_ERRORS_MESSAGE,
  CONTRIBUTION_PROCESSING_FAILED_MESSAGE,
  INVALID_FILE_MESSAGE,
} from '../abp-status-messages'

const { Title, Text } = Typography

interface Props {
  readonly clientId: string
}

// EMS-10420 — the "Analysis Upload" title's full date + Upcoming/Passed
// badge, derived from the same PolicyDto fields EMS-10326 already returns
// (benefitEffectiveDate, isOpenEnrollmentAvailable). displayDate/badge are
// both null in the pre-existing no-benefitEffectiveDate fallback, where the
// title shows only oepYear (the current calendar year).
interface OepTitleInfo {
  readonly oepYear: number
  readonly oepId: string | null
  readonly displayDate: string | null
  readonly badge: 'Upcoming' | 'Passed' | null
}

const CONSTRAINT_TAGS = ['.xlsx, .xlsm', 'Max 50 MB'] as const

const BUSY_STATES: ReadonlySet<OepUploadState> = new Set<OepUploadState>([
  'presigning',
  'uploading',
  'saving',
])

const NOT_AVAILABLE = '—'

interface AnalysisFileRow {
  readonly key: string
  readonly documentId: string
  readonly fileName: string
  readonly uploadedAt: string
  readonly uploadedBy: string | null
  readonly status: boolean
  // From the list endpoint's bulk lookup (EMS-10154, 2026-09-08) — the
  // starting point for the row components below; only a row already
  // reported as 'processing' here opens a live per-row poll for updates.
  readonly processingStatus: OepDocumentProcessingStatus
  // Why the processing job failed — only set for a 'failed' row whose job
  // stored a message; shown as the "Fail" tag's tooltip (EMS-10551).
  readonly failureReason: string | null
  // Every problem upload validation found — opened from "More details"
  // under the failure reason; null unless the file failed validation.
  readonly validationIssues: AnalysisFileValidationIssuesDto | null
  // Raw S3 key of the generated contribution file, or null if no
  // contribution model exists yet (2026-09-09) — gates the "Download
  // Contribution File" icon's visibility and enabled state.
  readonly contributionFileUrl: string | null
  // abp_contribution_model.use_current_model / .adjustments_made — null
  // until a contribution model has been generated for this document
  // (EMS-10154, 2026-09-14).
  readonly useCurrentModel: boolean | null
  readonly adjustmentsMade: boolean | null
  // True when contribution-model generation wrote an error workbook.
  // The storage key stays on the server — presign via handleDownloadError.
  readonly hasErrorFile: boolean
  // Human-readable summary of contribution-model generation errors, or
  // null when generation succeeded without errors.
  readonly errorMessage: string | null
  // The backend's "generated with errors" rule (EMS-11093).
  readonly hasGenerationErrors: boolean
  // ABP-4 / ABP-5 template, or null until the backend returns it (EMS-11022).
  readonly docType: OepDocumentDocType | null
}

const RowActionButtonBase = styled.span<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  border: 1px solid transparent;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ $disabled }) =>
      $disabled ? 'transparent' : 'currentColor'};
  }
`

const DownloadIconButton = styled(RowActionButtonBase)`
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
`

// The design's outlined Apply button (EMS-11022). Inline so it wins over
// antd's own button styles.
const APPLY_BUTTON_STYLE: React.CSSProperties = {
  borderRadius: 'var(--round-8, 8px)',
  border: 'var(--border-width, 1px) solid var(--border-default, #C3CFDE)',
  background: 'var(--background-white, #FFF)',
  fontSize: 12,
}

/** Case #3 — the contribution model was generated, but with errors. */
function hasContributionErrors(
  row: Pick<AnalysisFileRow, 'hasGenerationErrors'>
): boolean {
  return row.hasGenerationErrors
}

/**
 * An ABP-4 (EMS-11092) or ABP-5 (EMS-11022) file whose contribution model is
 * ready (the same rule ABP 5 generation uses) can become the current file.
 * Failed validation (case #1), errors (case #3), processing rows and files
 * with no doc type never offer Apply.
 */
function canApply(
  row: Pick<
    AnalysisFileRow,
    | 'docType'
    | 'processingStatus'
    | 'contributionFileUrl'
    | 'hasGenerationErrors'
  >
): boolean {
  return (
    (row.docType === 'abp-4' || row.docType === 'abp-5') &&
    isContributionModelReady(row)
  )
}

const DOC_TYPE_LABELS: Readonly<Record<OepDocumentDocType, string>> = {
  'abp-4': 'ABP 4',
  'abp-5': 'ABP 5',
}

// Pill under the upload / generate line (EMS-11022) — same shape and colors
// as the "Current" model badge (EMS-11070).
function DocTypeBadge({ docType }: { readonly docType: OepDocumentDocType }) {
  const { background, border, foreground } = MODEL_BADGE_COLORS.green
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
        height: 22,
        padding: '0 10px',
        borderRadius: 999,
        border: `1px solid ${border}`,
        background,
        color: foreground,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 'normal',
        whiteSpace: 'nowrap',
      }}
    >
      {DOC_TYPE_LABELS[docType]}
    </span>
  )
}

interface ApplyButtonProps {
  readonly isCurrent: boolean
  // The user has US access and the file can be applied.
  readonly isApplicable: boolean
  readonly isApplying: boolean
  // Another row action or an upload is in flight.
  readonly isDisabled: boolean
  readonly onApply: () => void
}

/**
 * The current file always shows a disabled "✓ Applied" — to every user, even
 * when it could not be applied today (ABP-4, no doc type, errors) — so the
 * file cells mark it. Apply shows only when the file is applicable; non-US
 * users never get it.
 */
function isApplyButtonShown({
  isCurrent,
  isApplicable,
}: Pick<ApplyButtonProps, 'isCurrent' | 'isApplicable'>): boolean {
  return isCurrent || isApplicable
}

/**
 * Apply / "✓ Applied" for the file it acts on (EMS-11070): the upload in the
 * Analysis File name cell, a generated ABP-5 in the ABP 5 File cell.
 */
function ApplyButton({
  isCurrent,
  isApplicable,
  isApplying,
  isDisabled,
  onApply,
}: ApplyButtonProps) {
  if (!isApplyButtonShown({ isCurrent, isApplicable })) return null
  return (
    <Button
      size="small"
      style={APPLY_BUTTON_STYLE}
      aria-label={isCurrent ? 'Applied' : 'Apply'}
      icon={isCurrent ? <CheckOutlined /> : undefined}
      loading={isApplying}
      disabled={isCurrent || (isDisabled && !isApplying)}
      onClick={(event) => {
        event.stopPropagation()
        onApply()
      }}
    >
      {isCurrent ? 'Applied' : 'Apply'}
    </Button>
  )
}

interface BadgeApplyRowProps {
  readonly badge: React.ReactNode
  readonly apply: ApplyButtonProps
}

// Badge, then Apply / "✓ Applied" right next to it on the same line
// (EMS-11070). Nothing renders when the cell has neither.
function BadgeApplyRow({ badge, apply }: BadgeApplyRowProps) {
  if (badge === null && !isApplyButtonShown(apply)) return null
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 4,
      }}
    >
      {badge}
      <ApplyButton {...apply} />
    </div>
  )
}

/** Last path segment of an S3 key / storage path — used as the display name. */
function fileNameFromStorageKey(key: string | null): string | null {
  if (key == null || key.length === 0) return null
  const parts = key.split('/')
  const last = parts[parts.length - 1]
  return last.length > 0 ? last : null
}

/**
 * Single-line file name that keeps the start and end visible when the cell is
 * too narrow: the head truncates with CSS ellipsis, the tail (incl. extension)
 * never shrinks. Full name stays in the tooltip.
 */
function MiddleEllipsisFileName({
  fileName,
  fontSize,
  lineHeight,
}: {
  readonly fileName: string
  readonly fontSize: number
  readonly lineHeight: number
}) {
  const lastDot = fileName.lastIndexOf('.')
  const hasExt = lastDot > 0 && lastDot < fileName.length - 1
  const base = hasExt ? fileName.slice(0, lastDot) : fileName
  const ext = hasExt ? fileName.slice(lastDot) : ''
  // Short names stay one node (end-ellipsis only). Longer names split so the
  // extension and trailing base stay visible while the head ellipsizes.
  const keepTailChars = Math.min(18, Math.max(10, Math.floor(base.length / 3)))
  const useMiddleSplit = base.length > keepTailChars + 8

  if (!useMiddleSplit) {
    return (
      <Tooltip title={fileName}>
        <span
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            fontSize,
            lineHeight: `${lineHeight}px`,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {fileName}
        </span>
      </Tooltip>
    )
  }

  const head = base.slice(0, base.length - keepTailChars)
  const tail = `${base.slice(base.length - keepTailChars)}${ext}`

  return (
    <Tooltip title={fileName}>
      <span
        style={{
          display: 'flex',
          flex: '1 1 auto',
          minWidth: 0,
          fontSize,
          lineHeight: `${lineHeight}px`,
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0,
          }}
        >
          {head}
        </span>
        <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>{tail}</span>
      </span>
    </Tooltip>
  )
}

interface GenerateAbpFileCellProps {
  readonly job: OepTargetFileJob | null
  readonly isLoading: boolean
  readonly isGenerating: boolean
  readonly isGenerateDisabled: boolean
  // Gates only the first-time Generate button — retry and Re-generate keep
  // their own job-based gating.
  readonly isContributionModelReady: boolean
  readonly fileNameLineHeight: number
  readonly fontSize: number
  readonly fontSizeSM: number
  readonly downloadColor: string
  readonly documentId: string
  // EMS-10969: false hides Generate, Re-generate and Download ABP file.
  readonly hasUsRestrictedDataAccess: boolean
  readonly onGenerate: () => void
  readonly onDownload: (jobId: string) => void
  // EMS-11070: Apply for the generated file — the document being applied,
  // whether another action blocks it, and the apply handler.
  readonly applyingDocumentId: string | null
  readonly isApplyDisabled: boolean
  readonly onApply: (documentId: string) => void
}

type AbpCellProps = Omit<
  GenerateAbpFileCellProps,
  'job' | 'isLoading' | 'isContributionModelReady' | 'documentId'
>

interface JobAbpCellProps extends AbpCellProps {
  readonly job: OepTargetFileJob
}

function CompletedAbpCell({
  job,
  isGenerating,
  isGenerateDisabled,
  fileNameLineHeight,
  fontSize,
  fontSizeSM,
  downloadColor,
  hasUsRestrictedDataAccess,
  onGenerate,
  onDownload,
  applyingDocumentId,
  isApplyDisabled,
  onApply,
}: JobAbpCellProps) {
  const uploadedAt = job.generatedAt ?? job.requestedAt
  const generatedDocumentId = job.generatedDocumentId ?? null
  const isCurrent = job.isCurrent === true
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minHeight: fileNameLineHeight,
          minWidth: 0,
        }}
      >
        <MiddleEllipsisFileName
          fileName={job.fileName}
          fontSize={fontSize}
          lineHeight={fileNameLineHeight}
        />
        {hasUsRestrictedDataAccess ? (
          <>
            <Tooltip title="Download ABP file">
              <DownloadIconButton
                role="button"
                aria-label="Download ABP file"
                onClick={(event) => {
                  event.stopPropagation()
                  onDownload(job.id)
                }}
                style={{ flexShrink: 0, color: downloadColor }}
              >
                <DownloadOutlined style={{ fontSize }} />
              </DownloadIconButton>
            </Tooltip>
            <Tooltip title="Re-generate ABP 5 file">
              <DownloadIconButton
                role="button"
                aria-label="Re-generate ABP 5 file"
                aria-disabled={isGenerating || isGenerateDisabled}
                $disabled={isGenerating || isGenerateDisabled}
                onClick={(event) => {
                  event.stopPropagation()
                  if (isGenerating || isGenerateDisabled) return
                  onGenerate()
                }}
                style={{ flexShrink: 0, color: downloadColor }}
              >
                <ReloadOutlined spin={isGenerating} style={{ fontSize }} />
              </DownloadIconButton>
            </Tooltip>
          </>
        ) : null}
      </div>
      <Text
        type="secondary"
        style={{ fontSize: fontSizeSM, lineHeight: '20px' }}
      >
        Generated by {job.requestedBy ?? NOT_AVAILABLE} on{' '}
        {OEPFormatters.formatDateTime(uploadedAt)}
      </Text>
      <BadgeApplyRow
        badge={<DocTypeBadge docType="abp-5" />}
        apply={{
          isCurrent,
          isApplicable:
            hasUsRestrictedDataAccess && generatedDocumentId !== null,
          isApplying:
            generatedDocumentId !== null &&
            applyingDocumentId === generatedDocumentId,
          isDisabled: isApplyDisabled,
          onApply: () => {
            if (generatedDocumentId !== null) onApply(generatedDocumentId)
          },
        }}
      />
    </div>
  )
}

function FailedAbpCell({
  job,
  isGenerating,
  isGenerateDisabled,
  fileNameLineHeight,
  fontSize,
  fontSizeSM,
  hasUsRestrictedDataAccess,
  onGenerate,
}: JobAbpCellProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Text
        type="danger"
        style={{ fontSize, lineHeight: `${fileNameLineHeight}px` }}
      >
        {ABP5_GENERATION_FAILED_MESSAGE}
      </Text>
      <Text
        type="secondary"
        style={{ fontSize: fontSizeSM, lineHeight: '20px' }}
      >
        Requested by {job.requestedBy ?? NOT_AVAILABLE} on{' '}
        {OEPFormatters.formatDateTime(job.requestedAt)}
      </Text>
      {hasUsRestrictedDataAccess ? (
        <Button
          type="primary"
          size="small"
          loading={isGenerating}
          disabled={isGenerateDisabled}
          onClick={(event) => {
            event.stopPropagation()
            onGenerate()
          }}
          style={{ alignSelf: 'flex-start' }}
        >
          Generate
        </Button>
      ) : null}
    </div>
  )
}

interface PendingAbpCellProps extends AbpCellProps {
  readonly documentId: string
  readonly isInFlight: boolean
  readonly isContributionModelReady: boolean
}

function PendingAbpCell({
  documentId,
  isInFlight,
  isGenerating,
  isGenerateDisabled,
  isContributionModelReady,
  fileNameLineHeight,
  fontSizeSM,
  hasUsRestrictedDataAccess,
  onGenerate,
}: PendingAbpCellProps) {
  if (!hasUsRestrictedDataAccess) {
    // EMS-10969: no Generate; still show that a job is running.
    return isInFlight ? (
      <Text
        type="secondary"
        style={{ fontSize: fontSizeSM, lineHeight: `${fileNameLineHeight}px` }}
      >
        Generating…
      </Text>
    ) : (
      <span style={{ lineHeight: `${fileNameLineHeight}px` }}>
        {NOT_AVAILABLE}
      </span>
    )
  }
  return (
    <Button
      id={`abp5-generate-btn-${documentId}`}
      type="primary"
      size="small"
      loading={isGenerating || isInFlight}
      disabled={isGenerateDisabled || !isContributionModelReady}
      onClick={(event) => {
        event.stopPropagation()
        onGenerate()
      }}
    >
      Generate
    </Button>
  )
}

function GenerateAbpFileCell({
  job,
  isLoading,
  isContributionModelReady,
  documentId,
  ...cellProps
}: GenerateAbpFileCellProps) {
  if (isLoading) {
    return <Spin size="small" />
  }
  // Only this analysis row's job — never paint another row's (or a
  // document-less) latest job onto every cell.
  const jobForThisRow =
    job !== null && job.documentId === documentId ? job : null

  if (jobForThisRow?.status === 'COMPLETED') {
    return <CompletedAbpCell job={jobForThisRow} {...cellProps} />
  }
  if (jobForThisRow?.status === 'FAILED') {
    return <FailedAbpCell job={jobForThisRow} {...cellProps} />
  }
  return (
    <PendingAbpCell
      documentId={documentId}
      isInFlight={
        jobForThisRow?.status === 'QUEUED' ||
        jobForThisRow?.status === 'PROCESSING'
      }
      isContributionModelReady={isContributionModelReady}
      {...cellProps}
    />
  )
}

const RowActionIconButton = styled.span<{ $disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  transition:
    background-color 0.15s ease,
    transform 0.15s ease;

  &:hover {
    background-color: ${({ $disabled }) =>
      $disabled ? 'transparent' : 'rgba(0, 0, 0, 0.06)'};
    transform: ${({ $disabled }) => ($disabled ? 'none' : 'scale(1.08)')};
  }
`

interface ContributionDownloadGating {
  // Hidden entirely until the analysis_file_uploaded job completes — before
  // that, no contribution model could possibly exist yet.
  readonly isVisible: boolean
  readonly isDisabled: boolean
  readonly isDownloading: boolean
}

/**
 * Single source of truth for the contribution-download action's
 * visibility/disabled/busy state, shared by the table row's icon and the
 * detail popup's footer button (EMS-10154) so the two can't silently drift
 * apart. `row` must be the *live* row from the `rows` array (recomputed every
 * render from the list query) — never a frozen snapshot — or this reports
 * stale gating while a background poll updates processingStatus/
 * contributionFileUrl out from under an already-open UI element.
 */
function getContributionDownloadGating(
  row: Pick<
    AnalysisFileRow,
    'documentId' | 'processingStatus' | 'contributionFileUrl'
  > | null,
  isActionDisabled: boolean,
  busy: ReturnType<typeof useOepDocumentRowActions>['busy']
): ContributionDownloadGating {
  if (row === null) {
    return { isVisible: false, isDisabled: true, isDownloading: false }
  }
  return {
    isVisible: row.processingStatus === 'completed',
    isDisabled: isActionDisabled || row.contributionFileUrl === null,
    isDownloading:
      busy?.documentId === row.documentId &&
      busy?.action === 'download-contribution',
  }
}

/** ABP 5 generation needs a contribution model that finished without errors. */
function isContributionModelReady(
  row: Pick<
    AnalysisFileRow,
    'processingStatus' | 'contributionFileUrl' | 'hasGenerationErrors'
  >
): boolean {
  return (
    row.processingStatus === 'completed' &&
    row.contributionFileUrl !== null &&
    !hasContributionErrors(row)
  )
}

interface ErrorDownloadGating {
  // Hidden until an error file exists for the row.
  readonly isVisible: boolean
  readonly isDisabled: boolean
  readonly isDownloading: boolean
}

/**
 * Single source of truth for the error-log download action's
 * visibility/disabled/busy state, shared by the table row's icon and the
 * detail popup's footer button so the two can't silently drift apart.
 * `row` must be the *live* row from the `rows` array — never a frozen
 * snapshot — or this reports stale gating while a background poll updates
 * hasErrorFile out from under an already-open UI element.
 */
function getErrorDownloadGating(
  row: Pick<AnalysisFileRow, 'documentId' | 'hasErrorFile'> | null,
  isActionDisabled: boolean,
  busy: ReturnType<typeof useOepDocumentRowActions>['busy']
): ErrorDownloadGating {
  if (row === null) {
    return { isVisible: false, isDisabled: true, isDownloading: false }
  }
  return {
    isVisible: row.hasErrorFile,
    isDisabled: isActionDisabled,
    isDownloading:
      busy?.documentId === row.documentId && busy?.action === 'download-error',
  }
}

function uploadProgressLabel(state: OepUploadState): string {
  if (state === 'presigning') return 'Getting upload URL…'
  if (state === 'uploading') return 'Uploading to S3…'
  if (state === 'saving') return 'Confirming upload…'
  return ''
}

interface ModelBadgeProps {
  readonly label: string
  readonly tooltip: string
  readonly tone?: 'green' | 'blue'
}

// Green = Current / Adjustment; blue = Proposed.
const MODEL_BADGE_COLORS = {
  green: { background: '#f6ffed', border: '#b7ea8f', foreground: '#379e0d' },
  blue: { background: '#e6f4ff', border: '#91caff', foreground: '#0958d9' },
} as const

const CIRCLE_CHECK_PATH =
  'M10.5 6C10.5 3.51472 8.48528 1.5 6 1.5C3.51472 1.5 1.5 3.51472 1.5 6C1.5 8.48528 3.51472 10.5 6 10.5C8.48528 10.5 10.5 8.48528 10.5 6ZM7.14648 4.64648C7.34175 4.45122 7.65825 4.45122 7.85352 4.64648C8.04878 4.84175 8.04878 5.15825 7.85352 5.35352L5.85352 7.35352C5.65825 7.54878 5.34175 7.54878 5.14648 7.35352L4.14648 6.35352C3.95122 6.15825 3.95122 5.84175 4.14648 5.64648C4.34175 5.45122 4.65825 5.45122 4.85352 5.64648L5.5 6.29297L7.14648 4.64648ZM11.5 6C11.5 9.03757 9.03757 11.5 6 11.5C2.96243 11.5 0.5 9.03757 0.5 6C0.5 2.96243 2.96243 0.5 6 0.5C9.03757 0.5 11.5 2.96243 11.5 6Z'

/**
 * Active model pill (Current / Proposed / Adjustment). Only rendered for
 * tags that apply to the sheet — inactive gray X badges are not shown.
 */
function ModelBadge({ label, tooltip, tone = 'green' }: ModelBadgeProps) {
  const { background, border, foreground } = MODEL_BADGE_COLORS[tone]
  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        height: 22,
        padding: '0 7px 0 5px',
        borderRadius: 999,
        background,
        border: `1px solid ${border}`,
        color: foreground,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: 'normal',
        whiteSpace: 'nowrap',
      }}
    >
      <svg
        width={12}
        height={12}
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
      >
        <path d={CIRCLE_CHECK_PATH} fill={foreground} />
      </svg>
      {label}
    </span>
  )
}

interface ContributionModelStatusProps {
  readonly row: AnalysisFileRow
}

// Model pills for a clean row. Failed (case #1) and error (case #3) rows show
// their message on the cell's first line instead.
function ContributionModelStatus({ row }: ContributionModelStatusProps) {
  const { token } = theme.useToken()
  const textStyle = { fontSize: token.fontSizeSM, lineHeight: '20px' }

  if (row.useCurrentModel !== null || row.adjustmentsMade === true) {
    return (
      <>
        {row.useCurrentModel === true ? (
          <ModelBadge label="Current" tooltip="Use current model" />
        ) : null}

        {row.useCurrentModel === false ? (
          <ModelBadge
            label="Proposed"
            tooltip="Use proposed model"
            tone="blue"
          />
        ) : null}

        {row.adjustmentsMade === true ? (
          <ModelBadge label="Adjustment" tooltip="Adjustments made" />
        ) : null}
      </>
    )
  }

  return (
    <Text
      id={`analysis-contribution-model-na-${row.documentId}`}
      type="secondary"
      style={textStyle}
    >
      {NOT_AVAILABLE}
    </Text>
  )
}

interface OepDocumentProcessingWatcherProps {
  readonly clientId: string
  readonly documentId: string
  readonly applied: boolean
  readonly initialProcessingStatus: OepDocumentProcessingStatus
}

/**
 * Keeps a processing row's list snapshot fresh after the Applied column
 * (which used to host this poll) is gone. The contribution and error
 * actions read processingStatus and contributionFileUrl from that list, so
 * a row the list already reports as completed or failed never polls.
 */
function OepDocumentProcessingWatcher({
  clientId,
  documentId,
  applied,
  initialProcessingStatus,
}: OepDocumentProcessingWatcherProps) {
  const isPolling = !applied && initialProcessingStatus === 'processing'
  const { processingStatus: polledProcessingStatus } =
    useOepDocumentProcessingStatus({
      clientId,
      documentId,
      enabled: isPolling,
    })

  const queryClient = useQueryClient()
  useEffect(() => {
    if (
      polledProcessingStatus !== undefined &&
      polledProcessingStatus !== initialProcessingStatus
    ) {
      void queryClient.invalidateQueries({
        queryKey: APPROVAL_QUERY_KEYS.oepDocumentList(clientId),
      })
    }
  }, [polledProcessingStatus, initialProcessingStatus, clientId, queryClient])

  return null
}

export function OepXlsxUploadSection({ clientId }: Props) {
  const { token } = theme.useToken()
  const { flags } = useFeatureFlags()
  const isDeleteEnabled = Boolean(
    flags['admin.abp.enable-delete-analysis-file']
  )
  const isGenerateAbp5FileEnabled = Boolean(
    flags['admin.abp-5.show-generate-abp5-file']
  )
  // Use the client's OEP plan year (same source the approval flow reads via
  // policy.planYear) — NOT the calendar year — so uploads line up there.
  const { data: titleInfo, isLoading: isPlanYearLoading } = useGetPolicyQuery(
    clientId,
    {
      // Match the approval flow's planYear derivation exactly, incl. its fallback
      // to the current calendar year when benefitEffectiveDate is missing
      // (EMS-10420 adds the full display date + Upcoming/Passed badge on top
      // of that same fallback — both null when there's no benefitEffectiveDate).
      select: (dto): OepTitleInfo =>
        dto.benefitEffectiveDate
          ? {
              oepYear: OEPFormatters.getYear(dto.benefitEffectiveDate),
              oepId: dto.oepId ?? null,
              displayDate: OEPFormatters.formatDateLongMonth(
                dto.benefitEffectiveDate
              ),
              // isOpenEnrollmentAvailable is unvalidated (PolicyDto is a cast,
              // not schema-checked, per policy.mappers.ts) — if this deploys
              // before EPS ships the field, it reads as undefined and falls
              // back to 'Passed' here, not a crash.
              badge: dto.isOpenEnrollmentAvailable ? 'Upcoming' : 'Passed',
            }
          : {
              oepYear: dayjs().year(),
              oepId: dto.oepId ?? null,
              displayDate: null,
              badge: null,
            },
    }
  )
  const oepYear = titleInfo?.oepYear ?? 0
  const oepId = titleInfo?.oepId ?? null
  const {
    uploadState,
    inlineError,
    handleFile,
    pendingDocuments,
    forgetPendingDocuments,
  } = useOepDocumentUpload({
    clientId,
    oepYear,
  })
  const {
    jobsByDocumentId: abp5JobsByDocumentId,
    isJobsByDocumentLoading: isAbp5JobLoading,
    isGenerating: isAbp5Generating,
    generatingDocumentId: abp5GeneratingDocumentId,
    isGenerateDisabled: isAbp5GenerateDisabled,
    handleGenerate: handleGenerateAbp5,
    handleDownload: handleDownloadAbp5,
  } = useAbp5FileJobs({
    clientId,
    enabled: isGenerateAbp5FileEnabled,
  })
  const {
    data: documents,
    isLoading: isLoadingList,
    isError: isListError,
  } = useOepDocumentList({ clientId, oepId })
  const {
    busy,
    handleApply,
    handleDelete,
    handleDownload,
    handleDownloadContribution,
    handleDownloadError,
  } = useOepDocumentRowActions({
    clientId,
  })
  // EMS-10967 / EMS-10969: these files can reveal US-restricted (Kaiser) data
  // and EPS answers 403 without UsRestrictedData:Manage, so hide every upload,
  // download, generate and delete control (no access while the check loads,
  // so the controls never flash).
  const { hasUsRestrictedDataAccess } = useStableUsRestrictedDataAccess()

  const isUploadBusy = BUSY_STATES.has(uploadState)

  const [detailRow, setDetailRow] = useState<AnalysisFileRow | null>(null)
  const [deleteRow, setDeleteRow] = useState<AnalysisFileRow | null>(null)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [validationIssuesRow, setValidationIssuesRow] =
    useState<AnalysisFileRow | null>(null)
  const closeValidationIssues = useCallback(
    () => setValidationIssuesRow(null),
    []
  )

  const isDeletePending =
    deleteRow !== null &&
    busy?.documentId === deleteRow.documentId &&
    busy.action === 'delete'

  const confirmDelete = async () => {
    if (deleteRow === null || isDeletePending) return
    try {
      await handleDelete(deleteRow.documentId)
      forgetPendingDocuments([deleteRow.documentId])
      setIsDeleteOpen(false)
    } catch {
      // Keep the confirmation modal open so the user can retry without
      // reopening it after a failed request.
    }
  }

  const openDeleteModal = (row: AnalysisFileRow) => {
    setDeleteRow(row)
    setIsDeleteOpen(true)
  }

  // Once the list returns an upload, its pending copy is no longer needed.
  useEffect(() => {
    if (documents === undefined) return
    forgetPendingDocuments(
      pendingDocuments
        .filter((pending) =>
          documents.some(
            (document) => document.documentId === pending.documentId
          )
        )
        .map((pending) => pending.documentId)
    )
  }, [documents, pendingDocuments, forgetPendingDocuments])

  // Uploads the list doesn't return yet go first (newest on top).
  const visibleDocuments = [
    ...pendingDocuments.filter(
      (pending) =>
        !(documents ?? []).some(
          (document) => document.documentId === pending.documentId
        )
    ),
    ...(documents ?? []),
  ]

  const rows: AnalysisFileRow[] = visibleDocuments.map((document) => ({
    key: document.documentId,
    documentId: document.documentId,
    fileName: document.filename,
    uploadedAt: document.uploadedAt,
    uploadedBy: document.uploadedBy,
    status: document.status,
    processingStatus: document.processingStatus,
    failureReason: document.failureReason,
    validationIssues: document.validationIssues ?? null,
    contributionFileUrl: document.contributionFileUrl,
    useCurrentModel: document.useCurrentModel,
    adjustmentsMade: document.adjustmentsMade,
    hasErrorFile: document.hasErrorFile,
    errorMessage: document.errorMessage,
    hasGenerationErrors: document.hasGenerationErrors,
    docType: document.docType ?? null,
  }))

  // Re-derived from the live `rows` array every render (never the frozen
  // `detailRow` snapshot itself) so the popup's footer button reflects a
  // processingStatus/contributionFileUrl change from a background poll while
  // the popup stays open — mirrors the table row's own live-update behavior.
  const liveDetailRow =
    detailRow !== null
      ? (rows.find((row) => row.documentId === detailRow.documentId) ??
        detailRow)
      : null
  const contributionGating = getContributionDownloadGating(
    liveDetailRow,
    isUploadBusy || busy !== null,
    busy
  )
  const errorGating = getErrorDownloadGating(
    liveDetailRow,
    isUploadBusy || busy !== null,
    busy
  )

  const fileNameLineHeight = Math.round(token.fontSize * token.lineHeight)
  // Analysis / Generate / Contribution Model share the row as file-style columns.
  const equalFileColumnWidth = isGenerateAbp5FileEnabled ? '32%' : '50%'

  const generateAbpFileColumn: ColumnsType<AnalysisFileRow>[number] = {
    title: 'ABP 5 File',
    key: 'generateAbpFile',
    width: equalFileColumnWidth,
    onCell: () => ({ style: { verticalAlign: 'top' } }),
    render: (_, row) => (
      <GenerateAbpFileCell
        job={abp5JobsByDocumentId.get(row.documentId) ?? null}
        documentId={row.documentId}
        isLoading={isAbp5JobLoading}
        isGenerating={
          isAbp5Generating && abp5GeneratingDocumentId === row.documentId
        }
        isGenerateDisabled={isAbp5GenerateDisabled}
        isContributionModelReady={isContributionModelReady(row)}
        fileNameLineHeight={fileNameLineHeight}
        fontSize={token.fontSize}
        fontSizeSM={token.fontSizeSM}
        downloadColor={token.colorTextSecondary}
        hasUsRestrictedDataAccess={hasUsRestrictedDataAccess}
        applyingDocumentId={busy?.action === 'apply' ? busy.documentId : null}
        isApplyDisabled={isUploadBusy || busy !== null}
        onApply={(documentId) => {
          void handleApply(documentId)
        }}
        onGenerate={() => {
          holdWindowScroll(() => {
            void handleGenerateAbp5(row.documentId)
          })
        }}
        onDownload={(jobId) => {
          holdWindowScroll(() => {
            void handleDownloadAbp5(jobId)
          })
        }}
      />
    ),
  }

  const columns: ColumnsType<AnalysisFileRow> = [
    {
      title: 'Analysis File name',
      key: 'fileName',
      width: equalFileColumnWidth,
      onCell: () => ({
        style: { verticalAlign: 'top', paddingLeft: 8 },
      }),
      render: (_, row) => {
        const isSettled = row.processingStatus !== 'processing'
        const isDownloadDisabled = isUploadBusy || busy !== null
        return (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <OepDocumentProcessingWatcher
              clientId={clientId}
              documentId={row.documentId}
              applied={row.status}
              initialProcessingStatus={row.processingStatus}
            />
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                minHeight: fileNameLineHeight,
                minWidth: 0,
              }}
            >
              <MiddleEllipsisFileName
                fileName={row.fileName}
                fontSize={token.fontSize}
                lineHeight={fileNameLineHeight}
              />
              {isSettled && hasUsRestrictedDataAccess ? (
                <Tooltip title="Download analysis file">
                  <DownloadIconButton
                    role="button"
                    aria-label="Download analysis file"
                    aria-disabled={isDownloadDisabled}
                    $disabled={isDownloadDisabled}
                    onClick={(event) => {
                      event.stopPropagation()
                      if (isDownloadDisabled) return
                      handleDownload(row.documentId)
                    }}
                    style={{
                      flexShrink: 0,
                      color: isDownloadDisabled
                        ? token.colorTextDisabled
                        : token.colorTextSecondary,
                    }}
                  >
                    <DownloadOutlined style={{ fontSize: token.fontSize }} />
                  </DownloadIconButton>
                </Tooltip>
              ) : null}
            </div>
            <Text
              type="secondary"
              style={{ fontSize: token.fontSizeSM, lineHeight: '20px' }}
            >
              Uploaded by {row.uploadedBy ?? NOT_AVAILABLE} on{' '}
              {OEPFormatters.formatDateTime(row.uploadedAt)}
            </Text>
            <BadgeApplyRow
              badge={
                row.docType !== null ? (
                  <DocTypeBadge docType={row.docType} />
                ) : null
              }
              apply={{
                isCurrent: row.status,
                isApplicable: hasUsRestrictedDataAccess && canApply(row),
                isApplying:
                  busy?.documentId === row.documentId &&
                  busy.action === 'apply',
                isDisabled: isDownloadDisabled,
                onApply: () => {
                  void handleApply(row.documentId)
                },
              }}
            />
          </div>
        )
      },
    },
    ...(isGenerateAbp5FileEnabled ? [generateAbpFileColumn] : []),
    {
      title: 'Contribution Model',
      key: 'contributionModel',
      width: equalFileColumnWidth,
      onCell: () => ({ style: { verticalAlign: 'top' } }),
      render: (_, row) => {
        const isActionDisabled = isUploadBusy || busy !== null
        const isProcessing = row.processingStatus === 'processing'
        const isFailed = row.processingStatus === 'failed'
        const isSettled = !isProcessing
        const contributionGating = getContributionDownloadGating(
          row,
          isActionDisabled,
          busy
        )
        const errorGating = getErrorDownloadGating(row, isActionDisabled, busy)
        const contributionFileName = fileNameFromStorageKey(
          row.contributionFileUrl
        )
        const hasErrors = !isFailed && hasContributionErrors(row)
        // Failed (case #1) and error (case #3) rows show only their message,
        // Delete and the error log — no file name, View or Download.
        const isClean = !isFailed && !hasErrors

        if (isProcessing) {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minHeight: fileNameLineHeight,
              }}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => {
                event.preventDefault()
              }}
            >
              <Spin size="small" />
              <Text
                type="secondary"
                style={{
                  flex: '1 1 auto',
                  fontSize: token.fontSizeSM,
                  lineHeight: '20px',
                }}
              >
                Processing…
              </Text>
              {/* No View while processing — there is no model to show yet. */}
            </div>
          )
        }

        return (
          <div
            style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => {
              // Focusing a control here scrolls the page to the top. Cancelling
              // mousedown keeps the click, and skips that focus scroll.
              event.preventDefault()
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                minHeight: fileNameLineHeight,
                minWidth: 0,
              }}
            >
              {isFailed ? (
                <Text
                  type="danger"
                  style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    fontSize: token.fontSize,
                    lineHeight: `${fileNameLineHeight}px`,
                  }}
                >
                  {row.validationIssues !== null ? (
                    <>
                      {INVALID_FILE_MESSAGE}{' '}
                      <button
                        id={`analysis-validation-details-btn-${row.documentId}`}
                        type="button"
                        className={validationIssuesStyles.detailsLink}
                        onClick={(event) => {
                          event.stopPropagation()
                          setValidationIssuesRow(row)
                        }}
                      >
                        see details
                      </button>
                    </>
                  ) : (
                    (row.failureReason ??
                    CONTRIBUTION_PROCESSING_FAILED_MESSAGE)
                  )}
                </Text>
              ) : hasErrors ? (
                <Text
                  id={`analysis-contribution-error-${row.documentId}`}
                  type="danger"
                  title={row.errorMessage ?? undefined}
                  style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    fontSize: token.fontSize,
                    lineHeight: `${fileNameLineHeight}px`,
                  }}
                >
                  {CONTRIBUTION_GENERATED_WITH_ERRORS_MESSAGE}
                </Text>
              ) : contributionFileName != null ? (
                <MiddleEllipsisFileName
                  fileName={contributionFileName}
                  fontSize={token.fontSize}
                  lineHeight={fileNameLineHeight}
                />
              ) : (
                <span
                  style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    lineHeight: `${fileNameLineHeight}px`,
                  }}
                >
                  {NOT_AVAILABLE}
                </span>
              )}
              {isClean ? (
                <Tooltip title="View contribution model">
                  <DownloadIconButton
                    role="button"
                    aria-label="View"
                    onClick={(event) => {
                      event.stopPropagation()
                      holdWindowScroll(() => setDetailRow(row))
                    }}
                    style={{
                      flexShrink: 0,
                      color: token.colorTextSecondary,
                    }}
                  >
                    <EyeOutlined style={{ fontSize: token.fontSize }} />
                  </DownloadIconButton>
                </Tooltip>
              ) : null}
              {isClean &&
              hasUsRestrictedDataAccess &&
              contributionGating.isVisible ? (
                <Tooltip title="Download contribution file">
                  <DownloadIconButton
                    role="button"
                    aria-label="Download"
                    tabIndex={contributionGating.isDisabled ? -1 : 0}
                    aria-disabled={contributionGating.isDisabled}
                    $disabled={contributionGating.isDisabled}
                    onClick={(event) => {
                      event.stopPropagation()
                      if (contributionGating.isDisabled) return
                      void handleDownloadContribution(row.documentId)
                    }}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      event.stopPropagation()
                      if (contributionGating.isDisabled) return
                      void handleDownloadContribution(row.documentId)
                    }}
                    style={{
                      flexShrink: 0,
                      color: contributionGating.isDownloading
                        ? token.colorPrimary
                        : contributionGating.isDisabled
                          ? token.colorTextDisabled
                          : token.colorTextSecondary,
                    }}
                  >
                    <DownloadOutlined style={{ fontSize: token.fontSize }} />
                  </DownloadIconButton>
                </Tooltip>
              ) : null}
              {isSettled && isDeleteEnabled && hasUsRestrictedDataAccess ? (
                <Tooltip title="Delete analysis file">
                  <DownloadIconButton
                    role="button"
                    aria-label="Delete"
                    aria-disabled={isActionDisabled}
                    $disabled={isActionDisabled}
                    onClick={(event) => {
                      event.stopPropagation()
                      if (isActionDisabled) return
                      holdWindowScroll(() => openDeleteModal(row))
                    }}
                    style={{
                      flexShrink: 0,
                      color: isActionDisabled
                        ? token.colorTextDisabled
                        : token.colorTextSecondary,
                    }}
                  >
                    <DeleteOutlined style={{ fontSize: token.fontSize }} />
                  </DownloadIconButton>
                </Tooltip>
              ) : null}
              {isSettled &&
              !isFailed &&
              hasUsRestrictedDataAccess &&
              errorGating.isVisible ? (
                <RowActionIconButton $disabled={errorGating.isDisabled}>
                  <FileExclamationOutlined
                    title="Download Error Log"
                    onClick={
                      errorGating.isDisabled
                        ? undefined
                        : () => handleDownloadError(row.documentId)
                    }
                    aria-disabled={errorGating.isDisabled}
                    style={{
                      color: errorGating.isDownloading
                        ? token.colorPrimary
                        : errorGating.isDisabled
                          ? token.colorTextDisabled
                          : token.colorError,
                      cursor: errorGating.isDisabled
                        ? 'not-allowed'
                        : 'pointer',
                      fontSize: 16,
                    }}
                  />
                </RowActionIconButton>
              ) : null}
            </div>
            {isClean ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 4,
                  minHeight: 20,
                }}
              >
                <ContributionModelStatus row={row} />
              </div>
            ) : null}
          </div>
        )
      },
    },
  ]

  return (
    <div>
      <Title level={5} style={{ marginBottom: 4 }}>
        Analysis Upload
        {titleInfo !== undefined
          ? ` – ${titleInfo.displayDate ?? titleInfo.oepYear}`
          : ''}
        {titleInfo?.badge != null && (
          <Tag
            color={titleInfo.badge === 'Upcoming' ? 'blue' : 'default'}
            style={{ marginLeft: 8 }}
          >
            {titleInfo.badge}
          </Tag>
        )}
      </Title>

      <Text
        type="secondary"
        style={{
          fontSize: token.fontSizeSM,
          display: 'block',
          marginBottom: 16,
        }}
      >
        Upload the completed ABP forecast/analysis spreadsheet. This file will
        be available for download in the Open Enrollment review interface.
      </Text>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', gap: 8 }}>
          {CONSTRAINT_TAGS.map((tag) => (
            <Tag
              key={tag}
              style={{
                borderRadius: 20,
                fontSize: token.fontSizeSM,
                padding: '1px 10px',
                color: token.colorTextSecondary,
                background: token.colorFillAlter,
                border: `1px solid ${token.colorBorder}`,
              }}
            >
              {tag}
            </Tag>
          ))}
        </div>

        {!hasUsRestrictedDataAccess ? null : isPlanYearLoading ? (
          <Spin size="small" />
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isUploadBusy && (
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {uploadProgressLabel(uploadState)}
              </Text>
            )}
            <Upload
              accept=".xlsx,.xlsm"
              maxCount={1}
              showUploadList={false}
              disabled={isUploadBusy}
              beforeUpload={(file) => {
                // handleFile never throws — errors surface via inlineError state
                void handleFile(file)
                return false
              }}
            >
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={isUploadBusy}
              >
                Upload
              </Button>
            </Upload>
          </div>
        )}
      </div>

      <DataTable<AnalysisFileRow>
        columns={columns}
        dataSource={rows}
        rowKey="key"
        loading={isLoadingList || isPlanYearLoading}
        emptyText="No analysis file uploaded yet."
        hidePagination
        tableLayout="fixed"
      />

      {(inlineError !== null || isListError) && (
        <Text
          type="danger"
          style={{
            fontSize: token.fontSizeSM,
            marginTop: 8,
            display: 'block',
          }}
        >
          {inlineError ?? 'Could not load the analysis files. Please refresh.'}
        </Text>
      )}

      <AnalysisFileDetailModal
        clientId={clientId}
        documentId={detailRow?.documentId ?? null}
        fileName={detailRow?.fileName ?? null}
        onClose={() => setDetailRow(null)}
        showDownloadContribution={
          hasUsRestrictedDataAccess && contributionGating.isVisible
        }
        isContributionDownloadDisabled={contributionGating.isDisabled}
        isDownloadingContribution={contributionGating.isDownloading}
        onDownloadContribution={() => {
          if (liveDetailRow !== null)
            handleDownloadContribution(liveDetailRow.documentId)
        }}
        hasErrorFile={errorGating.isVisible}
        showDownloadError={hasUsRestrictedDataAccess && errorGating.isVisible}
        isDownloadErrorDisabled={errorGating.isDisabled}
        isDownloadingError={errorGating.isDownloading}
        onDownloadError={() => {
          if (liveDetailRow !== null)
            handleDownloadError(liveDetailRow.documentId)
        }}
      />

      <AnalysisValidationIssuesModal
        fileName={validationIssuesRow?.fileName ?? null}
        validationIssues={validationIssuesRow?.validationIssues ?? null}
        onClose={closeValidationIssues}
      />

      <DeleteConfirmationModal
        open={isDeleteOpen}
        fileName={deleteRow?.fileName ?? null}
        confirmLoading={isDeletePending}
        onCancel={() => setIsDeleteOpen(false)}
        onConfirm={() => void confirmDelete()}
        afterClose={() => setDeleteRow(null)}
      />
    </div>
  )
}
